import React, { useState, useEffect } from 'react';
import { FiDownload, FiFile, FiTrash2, FiPlus, FiUpload, FiRefreshCw } from 'react-icons/fi';
import api, { getDownloadBaseUrl } from '../services/api';
import './DocumentosSection.css';

const DocumentosSection = ({ licencaId, empreendimentoId, processoLicenciamentoId, tarefaId, documentos, onRefresh, onDocumentAdded }) => {
  const [showModal, setShowModal] = useState(false);
  const [arquivos, setArquivos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [totalArquivos, setTotalArquivos] = useState(0);
  const [processados, setProcessados] = useState(0);
  const [reprocessingId, setReprocessingId] = useState(null);

  useEffect(() => {
    if (!uploading) return;
    setUploadProgress(0);
    const start = Date.now();
    const maxProgress = 90;
    const duration = 45000; // 45s para chegar a 90%
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const progress = Math.min(maxProgress, Math.floor((elapsed / duration) * maxProgress));
      setUploadProgress(progress);
    }, 500);
    return () => clearInterval(interval);
  }, [uploading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!arquivos || arquivos.length === 0) {
      alert('Selecione pelo menos um arquivo');
      return;
    }

    const arquivosParaEnviar = Array.isArray(arquivos) ? arquivos : Array.from(arquivos || []);

    setUploading(true);
    setTotalArquivos(arquivosParaEnviar.length);
    setProcessados(0);

    const erros = [];
    const avisos = [];
    const licencasCriadas = [];

    try {
      for (let i = 0; i < arquivosParaEnviar.length; i += 1) {
        const arquivoAtual = arquivosParaEnviar[i];
        try {
          const fd = new FormData();
          fd.append('arquivo', arquivoAtual);

          let res;
          if (tarefaId) {
            res = await api.post(`/api/tarefas/${tarefaId}/documentos`, fd);
          } else {
            if (licencaId) fd.append('licenca_id', String(licencaId));
            if (empreendimentoId) fd.append('empreendimento_id', String(empreendimentoId));
            if (processoLicenciamentoId) fd.append('processo_licenciamento_id', String(processoLicenciamentoId));
            res = await api.post('/api/documentos', fd);
          }

          const data = res?.data;
          if (data?.aviso_ia?.mensagem) {
            avisos.push(data.aviso_ia.mensagem);
          }
          if (data?.resultado === 'licenca_criada' && data?.licenca) {
            licencasCriadas.push(data.licenca);
            continue;
          }

          const doc = data?.documento || data;

          if (doc && onDocumentAdded) {
            onDocumentAdded(doc);
          }
        } catch (err) {
          console.error('Erro ao adicionar documento da fila:', err.response?.data || err);
          const msg = err.response?.data?.error || err.message || 'Erro ao salvar documento da fila';
          erros.push(msg);
        } finally {
          setProcessados((prev) => prev + 1);
        }
      }

      setUploadProgress(100);
      setShowModal(false);
      setArquivos([]);

      if (typeof onRefresh === 'function') {
        await onRefresh(tarefaId);
      }

      if (erros.length > 0) {
        alert(`Alguns documentos não puderam ser processados:\n- ${erros.join('\n- ')}`);
      }

      if (avisos.length > 0) {
        alert([...new Set(avisos)].join('\n\n'));
      }

      if (licencasCriadas.length === 1) {
        const abrirValidacao = window.confirm('Uma licença foi identificada automaticamente e enviada para extração. Deseja abrir a tela de validação agora?');
        if (abrirValidacao && licencasCriadas[0].redirect) {
          window.location.assign(licencasCriadas[0].redirect);
          return;
        }
      } else if (licencasCriadas.length > 1) {
        alert(`${licencasCriadas.length} licenças foram identificadas automaticamente e enviadas para extração. Acompanhe a validação pela tela de Licenças.`);
      }

      await new Promise((r) => setTimeout(r, 400));
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setTotalArquivos(0);
      setProcessados(0);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm('Excluir este documento?')) return;
    try {
      await api.delete(`/api/documentos/${doc.id}`);
      onRefresh?.(tarefaId);
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao excluir');
    }
  };

  const handleReprocess = async (doc) => {
    try {
      setReprocessingId(doc.id);
      const res = await api.post(`/api/documentos/${doc.id}/reprocessar`);
      const data = res.data || {};
      if (data.resultado === 'licenca_criada' && data.licenca) {
        await onRefresh?.(tarefaId);
        const abrirValidacao = window.confirm('O documento foi identificado como licença e enviado para extração. Deseja abrir a tela de validação agora?');
        if (abrirValidacao && data.licenca.redirect) window.location.assign(data.licenca.redirect);
        return;
      }
      await onRefresh?.(tarefaId);
      alert('Documento reprocessado com sucesso.');
    } catch (err) {
      const status = err.response?.status;
      const message = err.response?.data?.error;
      alert(message || (status === 404
        ? 'O recurso de reprocessamento ainda não está disponível no servidor. Atualize ou reinicie o backend e tente novamente.'
        : 'Não foi possível reprocessar o documento.'));
    } finally {
      setReprocessingId(null);
    }
  };

  const openModal = () => {
    setArquivos([]);
    setShowModal(true);
  };

  return (
    <div className="documentos-section">
      <div className="documentos-section-header">
        <span className="documentos-count">
          <FiFile className="documentos-count-icon" />
          {documentos?.length || 0} documento(s)
        </span>
        <button type="button" className="btn btn-sm btn-primary btn-add-doc" onClick={openModal}>
          <FiPlus /> Adicionar
        </button>
      </div>
      {(!documentos || documentos.length === 0) ? (
        <p className="text-muted">Nenhum documento vinculado.</p>
      ) : (
        <ul className="documentos-list">
          {documentos.map((doc) => (
            <li key={doc.id} className="documento-item">
              <div className="documento-item-icon">
                <FiFile />
              </div>
              <div className="documento-info">
                <strong className="documento-titulo">{doc.titulo}</strong>
                {doc.tipo && <span className="documento-tipo">{doc.tipo}</span>}
                {doc.descricao && <p className="documento-descricao">{doc.descricao}</p>}
                <small className="documento-descricao">
                  Documento: {doc.data_documento ? new Date(`${String(doc.data_documento).slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR') : '-'}
                  {' · '}Upload: {doc.criado_em ? new Date(doc.criado_em).toLocaleDateString('pt-BR') : '-'}
                </small>
              </div>
              <div className="documento-actions">
                {doc.arquivo && (
                  <a
                    href={`${getDownloadBaseUrl()}${doc.arquivo}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="documento-btn-download"
                    title="Baixar arquivo"
                  >
                    <FiDownload /> Baixar
                  </a>
                )}
                <button type="button" className="btn-doc-action btn-edit" onClick={() => handleReprocess(doc)} title="Reprocessar documento" disabled={reprocessingId === doc.id}>
                  <FiRefreshCw />
                </button>
                <button type="button" className="btn-doc-action btn-delete" onClick={() => handleDelete(doc)} title="Excluir">
                  <FiTrash2 />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => !uploading && setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Adicionar Documento</h3>
            <p className="documento-upload-hint">Envie o arquivo. A IA analisa o conteúdo e preenche automaticamente título, tipo e resumo.</p>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Arquivo *</label>
                <div className="documento-upload-zone">
                  <FiUpload className="documento-upload-icon" />
                  <input
                    type="file"
                    id="doc-upload"
                    multiple
                    onChange={(e) => setArquivos(Array.from(e.target.files || []))}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.zip,.shp,.shx,.dbf,.prj,.cpg,.qpj,.sbn,.sbx,.kml,.kmz,.geojson,.gpx"
                    disabled={uploading}
                  />
                  <span className="documento-upload-text">
                    {arquivos && arquivos.length > 1
                      ? `${arquivos.length} arquivos selecionados`
                      : arquivos && arquivos.length === 1
                        ? arquivos[0].name
                        : 'Clique ou arraste os arquivos'}
                  </span>
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)} disabled={uploading}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={!arquivos || arquivos.length === 0 || uploading}>
                  {uploading ? 'Enviando fila...' : 'Enviar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {uploading && (
        <div className="upload-processing-overlay" aria-busy="true" aria-live="polite">
          <div className="upload-processing-modal">
            <div className="upload-processing-spinner" aria-hidden="true" />
            <h2 className="upload-processing-title">Analisando documento com IA</h2>
            <p className="upload-processing-desc">
              O arquivo está sendo processado. A IA analisa o conteúdo e preenche automaticamente título, tipo e resumo. Aguarde a conclusão.
            </p>
            {totalArquivos > 1 && (
              <p className="upload-processing-desc">
                Processando {processados} de {totalArquivos} documento(s) na fila.
              </p>
            )}
            <div className="upload-processing-bar">
              <div className="upload-processing-bar-fill" style={{ width: `${uploadProgress}%` }} />
            </div>
            <p className="upload-processing-percent">{uploadProgress}%</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentosSection;
